import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Save, Upload, X, Plus, Trash2 } from 'lucide-react';
import { propertyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const PROPERTY_TYPES = ['Apartment', 'House', 'Loft', 'Studio', 'Villa', 'Cottage', 'Townhouse'];
const AMENITIES_LIST = [
  'Wifi', 'Kitchen', 'TV', 'Air Conditioning', 'Heating', 'Washer', 'Dryer',
  'Free Parking', 'Pool', 'Hot Tub', 'Gym', 'Workspace', 'Balcony', 'Garden',
  'BBQ', 'Fireplace', 'Dishwasher', 'Coffee Machine', 'Iron', 'Hair Dryer'
];

const EditPropertyPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAuthToken, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  
  const [property, setProperty] = useState({
    title: '',
    description: '',
    propertyType: 'Apartment',
    location: { address: '', city: '', postcode: '', country: 'United Kingdom' },
    capacity: { guests: 1, bedrooms: 1, beds: 1, bathrooms: 1 },
    pricing: { nightlyRate: 100, cleaningFee: 0, serviceFee: 0, weeklyDiscount: 0, monthlyDiscount: 0 },
    amenities: [],
    images: [],
    houseRules: '',
    checkInTime: '15:00',
    checkOutTime: '11:00',
    instantBook: false,
    minimumStay: 1,
    insurance: { hasInsurance: false, insurer: '', policyNumber: '', expiry: '' },
    councilLicenceNumber: '',
    councilBorough: '',
    isLeasehold: false,
    leaseholdConsentDeclared: false
  });

  const [newImageUrl, setNewImageUrl] = useState('');
  const [imageFiles, setImageFiles] = useState([]);

  // London 90-night rule (host-level): nights-used counter + planning consent.
  const [strInfo, setStrInfo] = useState(null);
  const [consentForm, setConsentForm] = useState({ hasPlanningConsent: false, reference: '' });
  const [consentSaving, setConsentSaving] = useState(false);

  useEffect(() => {
    if (authLoading || !isAuthenticated()) return;
    (async () => {
      try {
        const data = await propertyAPI.getStrCompliance(getAuthToken());
        setStrInfo(data);
        setConsentForm({
          hasPlanningConsent: !!data.planningConsent,
          reference: data.planningConsentRef || ''
        });
      } catch (_) {
        // Advisory panel only — never block the edit page on it.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const handleConsentSave = async () => {
    setConsentSaving(true);
    try {
      await propertyAPI.declarePlanningConsent(
        consentForm.hasPlanningConsent, consentForm.reference, getAuthToken()
      );
      toast({ title: 'Saved', description: 'Planning consent declaration updated.' });
      try { setStrInfo(await propertyAPI.getStrCompliance(getAuthToken())); } catch (_) {}
    } catch (error) {
      toast({ title: 'Error', description: 'Could not save the declaration', variant: 'destructive' });
    } finally {
      setConsentSaving(false);
    }
  };

  // Redirect if not authenticated — wait for auth to finish loading
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    fetchProperty();
  }, [authLoading, propertyId]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const data = await propertyAPI.getProperty(propertyId);
      setProperty({
        title: data.title || '',
        description: data.description || '',
        propertyType: data.propertyType || 'Apartment',
        location: data.location || { address: '', city: '', postcode: '', country: 'United Kingdom' },
        capacity: data.capacity || { guests: 1, bedrooms: 1, beds: 1, bathrooms: 1 },
        pricing: data.pricing || { nightlyRate: 100, cleaningFee: 0, serviceFee: 0, weeklyDiscount: 0, monthlyDiscount: 0 },
        amenities: data.amenities || [],
        images: data.images || [],
        houseRules: data.houseRules || '',
        checkInTime: data.checkInTime || '15:00',
        checkOutTime: data.checkOutTime || '11:00',
        instantBook: data.instantBook || false,
        minimumStay: data.minimumStay || 1,
        insurance: data.insurance || { hasInsurance: false, insurer: '', policyNumber: '', expiry: '' },
        councilLicenceNumber: data.councilLicenceNumber || '',
        councilBorough: data.councilBorough || '',
        isLeasehold: data.isLeasehold || false,
        leaseholdConsentDeclared: data.leaseholdConsentDeclared || false
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load property', variant: 'destructive' });
      navigate('/host/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!property.title.trim()) {
      toast({ title: 'Error', description: 'Property title is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      await propertyAPI.updateProperty(propertyId, property, token);
      
      // Upload any new image files
      if (imageFiles.length > 0) {
        await propertyAPI.uploadImages(propertyId, imageFiles, token);
      }

      toast({ title: 'Success', description: 'Property updated successfully' });
      navigate('/host/dashboard');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update property', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProperty(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setProperty(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleAmenity = (amenity) => {
    setProperty(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const addImageUrl = () => {
    if (newImageUrl.trim()) {
      setProperty(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setProperty(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
    
    // Create preview URLs
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProperty(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68]"></div>
      </div>
    );
  }

  const sections = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'location', label: 'Location' },
    { id: 'capacity', label: 'Capacity' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'amenities', label: 'Amenities' },
    { id: 'images', label: 'Images' },
    { id: 'rules', label: 'Rules & Settings' },
    { id: 'insurance', label: 'Insurance' },
    { id: 'compliance', label: 'Compliance' }
  ];

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/host/dashboard')}>
                <ArrowLeft size={20} />
              </Button>
              <Link to="/" className="flex items-center gap-3">
                <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
              </Link>
            </div>
            <h1 className="text-xl font-bold text-[#1F3A68]">Edit Property</h1>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1F3A68]">
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Section Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-[#1F3A68] text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Form Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Basic Info */}
            {activeSection === 'basic' && (
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Property Title *</Label>
                    <Input
                      value={property.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="e.g., Modern City Apartment"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={property.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe your property..."
                      rows={5}
                    />
                  </div>
                  <div>
                    <Label>Property Type</Label>
                    <select
                      value={property.propertyType}
                      onChange={(e) => handleChange('propertyType', e.target.value)}
                      className="w-full h-10 px-3 border rounded-lg"
                    >
                      {PROPERTY_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Location */}
            {activeSection === 'location' && (
              <Card>
                <CardHeader>
                  <CardTitle>Location</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Street Address</Label>
                    <Input
                      value={property.location.address}
                      onChange={(e) => handleNestedChange('location', 'address', e.target.value)}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={property.location.city}
                        onChange={(e) => handleNestedChange('location', 'city', e.target.value)}
                        placeholder="London"
                      />
                    </div>
                    <div>
                      <Label>Postcode</Label>
                      <Input
                        value={property.location.postcode}
                        onChange={(e) => handleNestedChange('location', 'postcode', e.target.value)}
                        placeholder="SW1A 1AA"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={property.location.country}
                      onChange={(e) => handleNestedChange('location', 'country', e.target.value)}
                      placeholder="United Kingdom"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Capacity */}
            {activeSection === 'capacity' && (
              <Card>
                <CardHeader>
                  <CardTitle>Capacity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Guests</Label>
                      <Input
                        type="number"
                        min="1"
                        value={property.capacity.guests}
                        onChange={(e) => handleNestedChange('capacity', 'guests', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div>
                      <Label>Bedrooms</Label>
                      <Input
                        type="number"
                        min="0"
                        value={property.capacity.bedrooms}
                        onChange={(e) => handleNestedChange('capacity', 'bedrooms', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Beds</Label>
                      <Input
                        type="number"
                        min="1"
                        value={property.capacity.beds}
                        onChange={(e) => handleNestedChange('capacity', 'beds', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div>
                      <Label>Bathrooms</Label>
                      <Input
                        type="number"
                        min="1"
                        step="0.5"
                        value={property.capacity.bathrooms}
                        onChange={(e) => handleNestedChange('capacity', 'bathrooms', parseFloat(e.target.value) || 1)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing */}
            {activeSection === 'pricing' && (
              <Card>
                <CardHeader>
                  <CardTitle>Pricing</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nightly Rate (£) *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={property.pricing.nightlyRate}
                        onChange={(e) => handleNestedChange('pricing', 'nightlyRate', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Cleaning Fee (£)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={property.pricing.cleaningFee}
                        onChange={(e) => handleNestedChange('pricing', 'cleaningFee', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Service Fee (£)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={property.pricing.serviceFee}
                        onChange={(e) => handleNestedChange('pricing', 'serviceFee', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Weekly Discount (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={property.pricing.weeklyDiscount}
                        onChange={(e) => handleNestedChange('pricing', 'weeklyDiscount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Monthly Discount (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={property.pricing.monthlyDiscount}
                        onChange={(e) => handleNestedChange('pricing', 'monthlyDiscount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Amenities */}
            {activeSection === 'amenities' && (
              <Card>
                <CardHeader>
                  <CardTitle>Amenities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {AMENITIES_LIST.map(amenity => (
                      <div
                        key={amenity}
                        onClick={() => toggleAmenity(amenity)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          property.amenities.includes(amenity)
                            ? 'bg-[#1F3A68] text-white border-[#1F3A68]'
                            : 'bg-white hover:border-[#1F3A68]'
                        }`}
                      >
                        {amenity}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Images */}
            {activeSection === 'images' && (
              <Card>
                <CardHeader>
                  <CardTitle>Property Images</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Images */}
                  {property.images.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {property.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Property ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={16} />
                          </button>
                          {index === 0 && (
                            <span className="absolute bottom-2 left-2 bg-[#1F3A68] text-white text-xs px-2 py-1 rounded">
                              Cover
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Image URL */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter image URL"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                    />
                    <Button onClick={addImageUrl} variant="outline">
                      <Plus size={16} className="mr-1" />
                      Add URL
                    </Button>
                  </div>

                  {/* Upload Images */}
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#1F3A68] transition-colors"
                    onClick={() => document.getElementById('edit-image-upload').click()}
                  >
                    <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                    <p className="text-gray-600">Click to upload images</p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="edit-image-upload"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rules & Settings */}
            {activeSection === 'rules' && (
              <Card>
                <CardHeader>
                  <CardTitle>Rules & Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>House Rules</Label>
                    <Textarea
                      value={property.houseRules}
                      onChange={(e) => handleChange('houseRules', e.target.value)}
                      placeholder="e.g., No smoking, No parties..."
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Check-in Time</Label>
                      <Input
                        type="time"
                        value={property.checkInTime}
                        onChange={(e) => handleChange('checkInTime', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Check-out Time</Label>
                      <Input
                        type="time"
                        value={property.checkOutTime}
                        onChange={(e) => handleChange('checkOutTime', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Minimum Stay (nights)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={property.minimumStay}
                        onChange={(e) => handleChange('minimumStay', parseInt(e.target.value) || 1)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Checkbox
                      id="instantBook"
                      checked={property.instantBook}
                      onCheckedChange={(checked) => handleChange('instantBook', checked)}
                    />
                    <div>
                      <Label htmlFor="instantBook" className="font-semibold cursor-pointer">
                        Instant Book
                      </Label>
                      <p className="text-sm text-gray-600">
                        Allow guests to book without prior approval
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Insurance (advisory) */}
            {activeSection === 'insurance' && (
              <Card>
                <CardHeader>
                  <CardTitle>Insurance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Checkbox
                      id="hasInsurance"
                      checked={property.insurance?.hasInsurance}
                      onCheckedChange={(checked) => handleNestedChange('insurance', 'hasInsurance', checked)}
                    />
                    <div>
                      <Label htmlFor="hasInsurance" className="font-semibold cursor-pointer">
                        I have short let insurance for this property
                      </Label>
                      <p className="text-sm text-gray-600">
                        Ariv does not provide insurance. You are responsible for holding cover suitable for short lets. Adding the details here is optional and just helps you keep track.
                      </p>
                    </div>
                  </div>
                  {property.insurance?.hasInsurance && (
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label>Insurer</Label>
                        <Input
                          value={property.insurance?.insurer || ''}
                          onChange={(e) => handleNestedChange('insurance', 'insurer', e.target.value)}
                          placeholder="e.g. Allianz"
                        />
                      </div>
                      <div>
                        <Label>Policy number</Label>
                        <Input
                          value={property.insurance?.policyNumber || ''}
                          onChange={(e) => handleNestedChange('insurance', 'policyNumber', e.target.value)}
                          placeholder="Policy number"
                        />
                      </div>
                      <div>
                        <Label>Expiry date</Label>
                        <Input
                          type="date"
                          value={property.insurance?.expiry || ''}
                          onChange={(e) => handleNestedChange('insurance', 'expiry', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Short-let compliance */}
            {activeSection === 'compliance' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Council licensing</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Some councils require short-let registration or a licence. If yours does,
                      record the details here — they stay on the listing record.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Council licence / registration number</Label>
                        <Input
                          value={property.councilLicenceNumber}
                          onChange={(e) => handleChange('councilLicenceNumber', e.target.value)}
                          placeholder="e.g. STL-2026-01234"
                        />
                      </div>
                      <div>
                        <Label>Council / borough</Label>
                        <Input
                          value={property.councilBorough}
                          onChange={(e) => handleChange('councilBorough', e.target.value)}
                          placeholder="e.g. London Borough of Camden"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Leasehold consent</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <Checkbox
                        id="isLeasehold"
                        checked={property.isLeasehold}
                        onCheckedChange={(checked) => handleChange('isLeasehold', !!checked)}
                      />
                      <Label htmlFor="isLeasehold" className="font-semibold cursor-pointer">
                        This property is leasehold
                      </Label>
                    </div>
                    {property.isLeasehold && (
                      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                        <Checkbox
                          id="leaseholdConsentDeclared"
                          checked={property.leaseholdConsentDeclared}
                          onCheckedChange={(checked) => handleChange('leaseholdConsentDeclared', !!checked)}
                        />
                        <div>
                          <Label htmlFor="leaseholdConsentDeclared" className="font-semibold cursor-pointer">
                            I declare my lease (or my freeholder) permits short-term letting
                          </Label>
                          <p className="text-sm text-gray-600">
                            Many leases restrict subletting or short lets. You are responsible for
                            checking yours before hosting.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>London 90-night rule</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {strInfo ? (
                      <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Nights let in {strInfo.referenceYear}</span>
                          <span className="font-semibold">
                            {strInfo.nightsLetThisYear} / {strInfo.cap}
                            {strInfo.status === 'exempt' && ' (exempt — planning consent declared)'}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              strInfo.status === 'over_cap' ? 'bg-red-500'
                                : strInfo.status === 'approaching' ? 'bg-amber-500'
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, strInfo.percentUsed)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          Entire homes in Greater London are capped at 90 let nights per calendar year
                          (Deregulation Act 2015) unless you hold planning permission. This is your
                          responsibility as host — Ariv shows the counter as an advisory.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nights counter unavailable.</p>
                    )}
                    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                      <Checkbox
                        id="hasPlanningConsent"
                        checked={consentForm.hasPlanningConsent}
                        onCheckedChange={(checked) => setConsentForm(p => ({ ...p, hasPlanningConsent: !!checked }))}
                      />
                      <div className="flex-1">
                        <Label htmlFor="hasPlanningConsent" className="font-semibold cursor-pointer">
                          I hold planning permission for short-term letting
                        </Label>
                        <p className="text-sm text-gray-600 mb-2">
                          Declaring consent exempts your account from the 90-night advisory. Keep your
                          evidence — Ariv may ask for it.
                        </p>
                        {consentForm.hasPlanningConsent && (
                          <Input
                            value={consentForm.reference}
                            onChange={(e) => setConsentForm(p => ({ ...p, reference: e.target.value }))}
                            placeholder="Planning reference (optional), e.g. 2026/1234/P"
                          />
                        )}
                      </div>
                    </div>
                    <Button onClick={handleConsentSave} disabled={consentSaving} variant="outline">
                      {consentSaving ? 'Saving…' : 'Save planning declaration'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <Button variant="outline" onClick={() => navigate('/host/dashboard')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#1F3A68]">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPropertyPage;
